import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// Dynamic slots resolved at per-client task render time. Keep in sync with
// _resolve_brief_slots() in apps/creative_studio/serializers.py.
export const BRIEF_SLOTS = [
  {
    key: 'client.ad_offer',
    label: 'Client AD offer',
    help: "Current AD offer label (e.g. \"50% Off Installation\").",
  },
  {
    key: 'client.logo',
    label: 'Client logo',
    help: 'Resolved to "<Client> logo" — designer places the real asset.',
  },
  {
    key: 'client.warranty',
    label: 'Warranty',
    help: "e.g. \"limited lifetime transferable warranty\". Auto-fillable from KB. Optional per client — empty collapses cleanly.",
  },
  {
    key: 'client.experience',
    label: 'Experience / years in business',
    help: "e.g. \"family-owned since 1986\". Auto-fillable from KB. Optional per client.",
  },
  {
    key: 'client.certifications',
    label: 'Certifications',
    help: "e.g. \"GAF Master Elite-certified installer\". Auto-fillable from KB. Optional per client.",
  },
  {
    key: 'client.install_quality',
    label: 'Install quality / approach',
    help: "e.g. \"factory-trained in-house crews — no subcontractors\". Auto-fillable from KB. Optional per client.",
  },
];

// Markdown textarea with a slot autocomplete: typing `{{` opens a popper of
// available substitution tokens, click/enter inserts e.g. `{{client.ad_offer}}`.
// Lives in /briefs/ + the flights page so designers + agency staff share one UX.
export default function BriefMarkdownEditor({
  value,
  onChange,
  minRows = 14,
  placeholder,
  helperText,
  ...textFieldProps
}) {
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [picker, setPicker] = useState({ open: false, openIdx: 0, caret: 0, query: '' });

  const updatePicker = useCallback((v, caret) => {
    // The trigger is `{{` *unclosed* before the caret. Bail out if the user has
    // already typed `}}` for the current run — they're past the slot.
    const before = v.slice(0, caret);
    const idx = before.lastIndexOf('{{');
    if (idx < 0) { setPicker({ open: false, openIdx: 0, caret: 0, query: '' }); return; }
    const run = before.slice(idx);
    if (run.includes('}}')) { setPicker({ open: false, openIdx: 0, caret: 0, query: '' }); return; }
    // A line break or `{` inside the run almost certainly means they aren't
    // actually picking a slot anymore — close.
    if (/\n/.test(run) || /\{[^{]/.test(run.slice(2))) {
      setPicker({ open: false, openIdx: 0, caret: 0, query: '' });
      return;
    }
    setPicker({ open: true, openIdx: idx, caret, query: run.slice(2).trim().toLowerCase() });
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    updatePicker(v, e.target.selectionStart || v.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && picker.open) {
      e.stopPropagation();
      setPicker({ open: false, openIdx: 0, caret: 0, query: '' });
    }
  };

  const insertSlot = (slot) => {
    const v = value || '';
    const before = v.slice(0, picker.openIdx);
    const after = v.slice(picker.caret);
    const insertion = `{{${slot.key}}}`;
    const next = `${before}${insertion}${after}`;
    onChange(next);
    setPicker({ open: false, openIdx: 0, caret: 0, query: '' });
    // Restore focus + put caret right after the inserted token so the designer
    // can keep typing without clicking back into the textarea.
    setTimeout(() => {
      const node = inputRef.current;
      if (!node) return;
      const pos = picker.openIdx + insertion.length;
      node.focus();
      try { node.setSelectionRange(pos, pos); } catch { /* noop */ }
    }, 0);
  };

  const filtered = useMemo(() => {
    if (!picker.query) return BRIEF_SLOTS;
    return BRIEF_SLOTS.filter((s) =>
      s.key.toLowerCase().includes(picker.query)
      || s.label.toLowerCase().includes(picker.query));
  }, [picker.query]);

  const showPopper = picker.open && filtered.length > 0;

  return (
    <Box ref={anchorRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        multiline
        minRows={minRows}
        fullWidth
        placeholder={placeholder}
        helperText={helperText ?? 'Type {{ to insert a dynamic client field.'}
        sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
        {...textFieldProps}
      />
      <Popper
        open={showPopper}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={() => setPicker({ open: false, openIdx: 0, caret: 0, query: '' })}>
          <Paper variant="outlined" sx={{ minWidth: 320, maxWidth: 480 }}>
            <MenuList autoFocus={false} dense sx={{ py: 0.5 }}>
              {filtered.map((s) => (
                <MenuItem
                  key={s.key}
                  // onMouseDown + preventDefault avoids stealing focus from the
                  // textarea, so the caret-restore in insertSlot() works.
                  onMouseDown={(e) => { e.preventDefault(); insertSlot(s); }}
                  sx={{ alignItems: 'flex-start', py: 0.75 }}
                >
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {`{{${s.key}}}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.help}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
